// What every motion helper needs to know about the page.
//
// BODY is the scroll container here (index.css gives html, body and #root a
// fixed 100% height), so window.scrollY never moves and window gets no scroll
// events. Read positions with getScrollTop() or getBoundingClientRect(), and
// subscribe with onScrollFrame().

import { useEffect, useState } from "react";

export const getScroller = () => document.body;

export const getScrollTop = () => document.body.scrollTop || document.documentElement.scrollTop || 0;

export const clamp01 = (n) => Math.min(Math.max(n, 0), 1);

const REDUCED = "(prefers-reduced-motion: reduce)";

export const prefersReducedMotion = () =>
    typeof window !== "undefined" && !!window.matchMedia?.(REDUCED)?.matches;

// One listener and one frame for the whole page, however many sections watch
// the scroll. `fn` runs at most once a frame, on page scroll and on resize.
// Rails scrolling sideways inside the page do not call it. Returns unsubscribe.
const subscribers = new Set();
let frame = 0;

const flush = () => {
    frame = 0;
    subscribers.forEach((fn) => fn());
};

const schedule = () => {
    if (!frame) frame = requestAnimationFrame(flush);
};

const onScroll = (e) => {
    const t = e.target;
    if (t === document || t === document.body || t === document.documentElement) schedule();
};

export function onScrollFrame(fn) {
    if (!subscribers.size) {
        document.addEventListener("scroll", onScroll, { capture: true, passive: true });
        window.addEventListener("resize", schedule);
    }
    subscribers.add(fn);
    return () => {
        subscribers.delete(fn);
        if (!subscribers.size) {
            document.removeEventListener("scroll", onScroll, { capture: true });
            window.removeEventListener("resize", schedule);
            cancelAnimationFrame(frame);
            frame = 0;
        }
    };
}

// A media query as state, following changes while the page is open.
export function useMediaQuery(query) {
    const [matches, setMatches] = useState(() => !!window.matchMedia?.(query)?.matches);
    useEffect(() => {
        const mq = window.matchMedia?.(query);
        if (!mq) return undefined;
        const onChange = (e) => setMatches(e.matches);
        setMatches(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, [query]);
    return matches;
}

// True unless the visitor asked for reduced motion.
export function useMotionAllowed() {
    return !useMediaQuery(REDUCED);
}

// The reference's two layouts: desktop is 1024 and up.
export const DESKTOP = "(min-width: 1024px)";
export const FINE_POINTER = "(hover: hover) and (pointer: fine)";
